// src/pages/CRM/SFA/PriceBookList.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, Chip, Stack, Alert, LinearProgress,
  Card, CardContent, Divider, Collapse, InputAdornment,
  Autocomplete, Tooltip, Menu, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as BackIcon,
  LibraryBooks as PriceBookIcon,
  Inventory as ProductIcon,
  Layers as TiersIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import { formatCurrency, Currencies } from './sfaEnums';
import { priceBookService, productService } from './sfaService';

const PriceBookList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [priceBooks, setPriceBooks] = useState([]);
  const [products, setProducts] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState([]);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Dialogs
  const [priceBookDialogOpen, setPriceBookDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form data
  const [editingPriceBook, setEditingPriceBook] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingPriceBook, setDeletingPriceBook] = useState(null);
  const [selectedPriceBookId, setSelectedPriceBookId] = useState(null);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPriceBook, setMenuPriceBook] = useState(null);

  // Price book form
  const [priceBookForm, setPriceBookForm] = useState({
    name: '',
    description: '',
    is_standard: false,
    is_active: true,
    currency: 'USD',
    region: '',
    customer_tier: '',
    start_date: null,
    end_date: null
  });

  // Entry form
  const [entryForm, setEntryForm] = useState({
    product_id: null,
    list_price: 0,
    use_standard_price: false,
    min_price: '',
    max_discount_percent: '',
    is_active: true,
    has_tiered_pricing: false
  });
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Tier management state
  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState({
    min_quantity: '',
    max_quantity: '',
    unit_price: '',
    tier_name: ''
  });
  const [editingTierIndex, setEditingTierIndex] = useState(null);
  const [tiersLoading, setTiersLoading] = useState(false);

  // Check permissions
  useEffect(() => {
    const checkPerms = async () => {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    };
    checkPerms();
  }, []);

  // Fetch data
  const fetchPriceBooks = async () => {
    setLoading(true);
    try {
      const response = await priceBookService.getAll();
      if (response.success) {
        setPriceBooks(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching price books:', error);
      enqueueSnackbar('Failed to load price books', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getAll({ status: 'ACTIVE', per_page: 200 });
      if (response.success) {
        setProducts(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchPriceBookEntries = async (priceBookId) => {
    try {
      const response = await priceBookService.getById(priceBookId);
      if (response.success) {
        setExpandedEntries(response.data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching price book entries:', error);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchPriceBooks();
      fetchProducts();
    }
  }, [canView]);

  // Handlers
  const handleExpand = (priceBookId) => {
    if (expandedId === priceBookId) {
      setExpandedId(null);
      setExpandedEntries([]);
    } else {
      setExpandedId(priceBookId);
      fetchPriceBookEntries(priceBookId);
    }
  };

  const openPriceBookDialog = (priceBook = null) => {
    if (priceBook) {
      setEditingPriceBook(priceBook);
      setPriceBookForm({
        name: priceBook.name || '',
        description: priceBook.description || '',
        is_standard: priceBook.is_standard || false,
        is_active: priceBook.is_active !== false,
        currency: priceBook.currency || 'USD',
        region: priceBook.region || '',
        customer_tier: priceBook.customer_tier || '',
        start_date: priceBook.start_date ? new Date(priceBook.start_date) : null,
        end_date: priceBook.end_date ? new Date(priceBook.end_date) : null
      });
    } else {
      setEditingPriceBook(null);
      setPriceBookForm({
        name: '',
        description: '',
        is_standard: false,
        is_active: true,
        currency: 'USD',
        region: '',
        customer_tier: '',
        start_date: null,
        end_date: null
      });
    }
    setPriceBookDialogOpen(true);
    handleMenuClose();
  };

  const openEntryDialog = async (priceBookId, entry = null) => {
    setSelectedPriceBookId(priceBookId);
    setTiers([]);
    setTierForm({ min_quantity: '', max_quantity: '', unit_price: '', tier_name: '' });
    setEditingTierIndex(null);

    if (entry) {
      setEditingEntry(entry);
      setSelectedProduct(entry.product || null);
      setEntryForm({
        product_id: entry.product_id,
        list_price: entry.list_price || 0,
        use_standard_price: entry.use_standard_price || false,
        min_price: entry.min_price || '',
        max_discount_percent: entry.max_discount_percent || '',
        is_active: entry.is_active !== false,
        has_tiered_pricing: entry.has_tiered_pricing || false
      });

      // Load existing tiers if tiered pricing is enabled
      if (entry.has_tiered_pricing) {
        setTiersLoading(true);
        try {
          // Tiers should be included in entry response
          setTiers(entry.tiers || []);
        } finally {
          setTiersLoading(false);
        }
      }
    } else {
      setEditingEntry(null);
      setSelectedProduct(null);
      setEntryForm({
        product_id: null,
        list_price: 0,
        use_standard_price: false,
        min_price: '',
        max_discount_percent: '',
        is_active: true,
        has_tiered_pricing: false
      });
    }
    setEntryDialogOpen(true);
  };

  const handleMenuOpen = (event, priceBook) => {
    setMenuAnchor(event.currentTarget);
    setMenuPriceBook(priceBook);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPriceBook(null);
  };

  const handleSavePriceBook = async () => {
    if (!priceBookForm.name) {
      enqueueSnackbar('Name is required', { variant: 'error' });
      return;
    }

    try {
      const payload = {
        ...priceBookForm,
        start_date: priceBookForm.start_date ? priceBookForm.start_date.toISOString().split('T')[0] : null,
        end_date: priceBookForm.end_date ? priceBookForm.end_date.toISOString().split('T')[0] : null
      };

      if (editingPriceBook) {
        await priceBookService.update(editingPriceBook.id, payload);
        enqueueSnackbar('Price book updated', { variant: 'success' });
      } else {
        await priceBookService.create(payload);
        enqueueSnackbar('Price book created', { variant: 'success' });
      }

      setPriceBookDialogOpen(false);
      fetchPriceBooks();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to save price book', { variant: 'error' });
    }
  };

  const handleDeletePriceBook = async () => {
    if (!deletingPriceBook) return;

    try {
      await priceBookService.delete(deletingPriceBook.id);
      enqueueSnackbar('Price book deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingPriceBook(null);
      fetchPriceBooks();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to delete price book', { variant: 'error' });
    }
  };

  const handleSaveEntry = async () => {
    if (!entryForm.product_id && !editingEntry) {
      enqueueSnackbar('Product is required', { variant: 'error' });
      return;
    }

    // Validate tiers if tiered pricing is enabled
    if (entryForm.has_tiered_pricing && tiers.length === 0) {
      enqueueSnackbar('Please add at least one pricing tier', { variant: 'error' });
      return;
    }

    try {
      const payload = {
        product_id: entryForm.product_id,
        list_price: parseFloat(entryForm.list_price) || 0,
        use_standard_price: entryForm.use_standard_price,
        min_price: entryForm.min_price ? parseFloat(entryForm.min_price) : null,
        max_discount_percent: entryForm.max_discount_percent ? parseFloat(entryForm.max_discount_percent) : null,
        is_active: entryForm.is_active,
        has_tiered_pricing: entryForm.has_tiered_pricing,
        tiers: entryForm.has_tiered_pricing ? tiers.map(t => ({
          min_quantity: parseInt(t.min_quantity),
          max_quantity: t.max_quantity ? parseInt(t.max_quantity) : null,
          unit_price: parseFloat(t.unit_price),
          tier_name: t.tier_name || ''
        })) : []
      };

      if (editingEntry) {
        await priceBookService.updateEntry(selectedPriceBookId, editingEntry.id, payload);
        enqueueSnackbar('Entry updated', { variant: 'success' });
      } else {
        await priceBookService.addEntry(selectedPriceBookId, payload);
        enqueueSnackbar('Product added to price book', { variant: 'success' });
      }

      setEntryDialogOpen(false);
      fetchPriceBookEntries(selectedPriceBookId);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to save entry', { variant: 'error' });
    }
  };

  const handleDeleteEntry = async (priceBookId, entryId) => {
    try {
      await priceBookService.removeEntry(priceBookId, entryId);
      enqueueSnackbar('Entry removed', { variant: 'success' });
      fetchPriceBookEntries(priceBookId);
    } catch (error) {
      enqueueSnackbar('Failed to remove entry', { variant: 'error' });
    }
  };

  // Tier management handlers
  const handleAddTier = () => {
    if (!tierForm.min_quantity || !tierForm.unit_price) {
      enqueueSnackbar('Minimum quantity and unit price are required', { variant: 'warning' });
      return;
    }

    const minQty = parseInt(tierForm.min_quantity);
    const maxQty = tierForm.max_quantity ? parseInt(tierForm.max_quantity) : null;
    const unitPrice = parseFloat(tierForm.unit_price);

    // Validate tier doesn't overlap with existing tiers
    const hasOverlap = tiers.some((t, idx) => {
      if (editingTierIndex !== null && idx === editingTierIndex) return false;
      const existingMin = t.min_quantity;
      const existingMax = t.max_quantity || Infinity;
      const newMax = maxQty || Infinity;
      return (minQty >= existingMin && minQty <= existingMax) ||
             (newMax >= existingMin && newMax <= existingMax) ||
             (minQty <= existingMin && newMax >= existingMax);
    });

    if (hasOverlap) {
      enqueueSnackbar('This tier overlaps with an existing tier', { variant: 'error' });
      return;
    }

    if (editingTierIndex !== null) {
      // Update existing tier
      setTiers(prev => prev.map((t, idx) =>
        idx === editingTierIndex
          ? { ...t, min_quantity: minQty, max_quantity: maxQty, unit_price: unitPrice, tier_name: tierForm.tier_name }
          : t
      ));
      setEditingTierIndex(null);
    } else {
      // Add new tier
      setTiers(prev => [...prev, {
        min_quantity: minQty,
        max_quantity: maxQty,
        unit_price: unitPrice,
        tier_name: tierForm.tier_name
      }].sort((a, b) => a.min_quantity - b.min_quantity));
    }

    // Reset form
    setTierForm({ min_quantity: '', max_quantity: '', unit_price: '', tier_name: '' });
  };

  const handleEditTier = (index) => {
    const tier = tiers[index];
    setTierForm({
      min_quantity: tier.min_quantity.toString(),
      max_quantity: tier.max_quantity ? tier.max_quantity.toString() : '',
      unit_price: tier.unit_price.toString(),
      tier_name: tier.tier_name || ''
    });
    setEditingTierIndex(index);
  };

  const handleDeleteTier = (index) => {
    setTiers(prev => prev.filter((_, idx) => idx !== index));
    if (editingTierIndex === index) {
      setEditingTierIndex(null);
      setTierForm({ min_quantity: '', max_quantity: '', unit_price: '', tier_name: '' });
    }
  };

  const handleCancelTierEdit = () => {
    setEditingTierIndex(null);
    setTierForm({ min_quantity: '', max_quantity: '', unit_price: '', tier_name: '' });
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view price books.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, mt: 10 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/products`)}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                <PriceBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Price Books
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage pricing for different regions and customer tiers
              </Typography>
            </Box>
          </Box>
          {canModify && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openPriceBookDialog()}
            >
              New Price Book
            </Button>
          )}
        </Box>

        {loading ? (
          <LinearProgress sx={{ mb: 3 }} />
        ) : priceBooks.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PriceBookIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No Price Books Yet</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Create price books to manage different pricing for regions or customer tiers.
            </Typography>
            {canModify && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openPriceBookDialog()}>
                Create First Price Book
              </Button>
            )}
          </Paper>
        ) : (
          <Stack spacing={2}>
            {priceBooks.map((priceBook) => (
              <Card key={priceBook.id} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton onClick={() => handleExpand(priceBook.id)}>
                        {expandedId === priceBook.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6">{priceBook.name}</Typography>
                          {priceBook.is_standard && (
                            <Chip icon={<StarIcon />} label="Standard" color="primary" size="small" />
                          )}
                          {!priceBook.is_active && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {priceBook.description}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          {priceBook.currency && (
                            <Chip label={priceBook.currency} size="small" variant="outlined" />
                          )}
                          {priceBook.region && (
                            <Chip label={priceBook.region} size="small" variant="outlined" />
                          )}
                          {priceBook.customer_tier && (
                            <Chip label={priceBook.customer_tier} size="small" variant="outlined" />
                          )}
                          <Chip
                            icon={<ProductIcon />}
                            label={`${priceBook.entry_count || 0} products`}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    </Box>
                    {canModify && (
                      <IconButton onClick={(e) => handleMenuOpen(e, priceBook)}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>

                {/* Expanded entries */}
                <Collapse in={expandedId === priceBook.id}>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Products in this Price Book</Typography>
                      {canModify && (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => openEntryDialog(priceBook.id)}>
                          Add Product
                        </Button>
                      )}
                    </Box>

                    {expandedEntries.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">List Price</TableCell>
                              <TableCell align="right">Min Price</TableCell>
                              <TableCell align="right">Max Discount</TableCell>
                              <TableCell align="center">Status</TableCell>
                              {canModify && <TableCell align="center">Actions</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {expandedEntries.map((entry) => (
                              <TableRow key={entry.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {entry.product_name}
                                    {entry.has_tiered_pricing && (
                                      <Tooltip title="Volume/Tiered Pricing Enabled">
                                        <Chip icon={<TiersIcon />} label="Tiered" size="small" color="info" variant="outlined" />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>{entry.product_sku}</TableCell>
                                <TableCell align="right">
                                  {entry.use_standard_price ? (
                                    <Tooltip title="Uses standard price">
                                      <Chip label="Standard" size="small" />
                                    </Tooltip>
                                  ) : (
                                    formatCurrency(entry.list_price)
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {entry.min_price ? formatCurrency(entry.min_price) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {entry.max_discount_percent ? `${entry.max_discount_percent}%` : '-'}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={entry.is_active ? 'Active' : 'Inactive'}
                                    color={entry.is_active ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                {canModify && (
                                  <TableCell align="center">
                                    <IconButton size="small" onClick={() => openEntryDialog(priceBook.id, entry)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteEntry(priceBook.id, entry.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No products in this price book yet.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Card>
            ))}
          </Stack>
        )}

        {/* Context Menu */}
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
          <MenuItem onClick={() => openPriceBookDialog(menuPriceBook)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          {!menuPriceBook?.is_standard && (
            <MenuItem
              onClick={() => {
                setDeletingPriceBook(menuPriceBook);
                setDeleteDialogOpen(true);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          )}
        </Menu>

        {/* Price Book Dialog */}
        <Dialog open={priceBookDialogOpen} onClose={() => setPriceBookDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingPriceBook ? 'Edit Price Book' : 'New Price Book'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  required
                  value={priceBookForm.name}
                  onChange={(e) => setPriceBookForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={priceBookForm.description}
                  onChange={(e) => setPriceBookForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  value={priceBookForm.currency}
                  onChange={(e) => setPriceBookForm(prev => ({ ...prev, currency: e.target.value }))}
                >
                  {Object.entries(Currencies).map(([code, currencyData]) => (
                    <MenuItem key={code} value={code}>
                      {typeof currencyData === 'object' 
                        ? `${code} - ${currencyData.name}` 
                        : `${code} - ${currencyData}`
                      }
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Region"
                  value={priceBookForm.region}
                  onChange={(e) => setPriceBookForm(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="e.g., North America"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Customer Tier"
                  value={priceBookForm.customer_tier}
                  onChange={(e) => setPriceBookForm(prev => ({ ...prev, customer_tier: e.target.value }))}
                  placeholder="e.g., Enterprise"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={priceBookForm.is_active}
                      onChange={(e) => setPriceBookForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={priceBookForm.start_date}
                  onChange={(date) => setPriceBookForm(prev => ({ ...prev, start_date: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={priceBookForm.end_date}
                  onChange={(date) => setPriceBookForm(prev => ({ ...prev, end_date: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={priceBookForm.is_standard}
                      onChange={(e) => setPriceBookForm(prev => ({ ...prev, is_standard: e.target.checked }))}
                    />
                  }
                  label="Set as Standard Price Book"
                />
                {priceBookForm.is_standard && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    This will unset any existing standard price book.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPriceBookDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSavePriceBook}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Entry Dialog */}
        <Dialog open={entryDialogOpen} onClose={() => setEntryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingEntry ? 'Edit Product Entry' : 'Add Product to Price Book'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {!editingEntry && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => `${option.name} (${option.sku || 'No SKU'})`}
                    value={selectedProduct}
                    onChange={(e, newValue) => {
                      setSelectedProduct(newValue);
                      setEntryForm(prev => ({
                        ...prev,
                        product_id: newValue?.id || null,
                        list_price: newValue?.standard_price || 0
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Product" required />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={entryForm.use_standard_price}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, use_standard_price: e.target.checked }))}
                    />
                  }
                  label="Use Standard Price"
                />
              </Grid>
              {!entryForm.use_standard_price && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="List Price"
                    type="number"
                    value={entryForm.list_price}
                    onChange={(e) => setEntryForm(prev => ({ ...prev, list_price: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum Price"
                  type="number"
                  value={entryForm.min_price}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, min_price: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  helperText="Floor price for discounts"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max Discount"
                  type="number"
                  value={entryForm.max_discount_percent}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, max_discount_percent: e.target.value }))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  helperText="Maximum allowed discount"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={entryForm.is_active}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={entryForm.has_tiered_pricing}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, has_tiered_pricing: e.target.checked }))}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TiersIcon fontSize="small" />
                      Enable Volume/Tiered Pricing
                    </Box>
                  }
                />
              </Grid>

              {/* Tiered Pricing Section */}
              {entryForm.has_tiered_pricing && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TiersIcon /> Pricing Tiers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define quantity-based pricing tiers. Price will be applied based on order quantity.
                  </Typography>

                  {/* Tier form */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Min Qty"
                          type="number"
                          required
                          value={tierForm.min_quantity}
                          onChange={(e) => setTierForm(prev => ({ ...prev, min_quantity: e.target.value }))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Max Qty"
                          type="number"
                          value={tierForm.max_quantity}
                          onChange={(e) => setTierForm(prev => ({ ...prev, max_quantity: e.target.value }))}
                          placeholder="Unlimited"
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Unit Price"
                          type="number"
                          required
                          value={tierForm.unit_price}
                          onChange={(e) => setTierForm(prev => ({ ...prev, unit_price: e.target.value }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Tier Name"
                          value={tierForm.tier_name}
                          onChange={(e) => setTierForm(prev => ({ ...prev, tier_name: e.target.value }))}
                          placeholder="e.g., Bronze"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddTier}
                            startIcon={editingTierIndex !== null ? <EditIcon /> : <AddIcon />}
                          >
                            {editingTierIndex !== null ? 'Update' : 'Add'}
                          </Button>
                          {editingTierIndex !== null && (
                            <Button size="small" onClick={handleCancelTierEdit}>
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Tiers table */}
                  {tiersLoading ? (
                    <LinearProgress sx={{ my: 2 }} />
                  ) : tiers.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tier Name</TableCell>
                            <TableCell align="center">Quantity Range</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tiers.map((tier, index) => (
                            <TableRow key={index} sx={{ bgcolor: editingTierIndex === index ? 'action.selected' : 'inherit' }}>
                              <TableCell>
                                {tier.tier_name || (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Tier {index + 1}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={tier.max_quantity ? `${tier.min_quantity} - ${tier.max_quantity}` : `${tier.min_quantity}+`}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>
                                  {formatCurrency(tier.unit_price)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" onClick={() => handleEditTier(index)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteTier(index)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No pricing tiers defined yet. Add tiers above to enable volume-based pricing.
                    </Alert>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEntryDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEntry}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Price Book</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{deletingPriceBook?.name}"? This will also remove all product entries.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeletePriceBook}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PriceBookList;